import React, { useEffect, useState } from "react";
import { BookOpen } from "lucide-react";
import { API_BASE_URL } from "../../config/apiConfig";
import { useNavigate } from "react-router-dom";
import bg from "../../assets/first_bg.png";
import { toast } from "sonner";
import { sortClasses } from "../../utils/classOrder";

const FreeUsersOnboardingPage = () => {
    const [formData, setFormData] = useState({
        yearGroup: "",
        subjects: [],
    });

    const [questions, setQuestions] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchQuestions = async () => {
            try {
                const res = await fetch(`${API_BASE_URL}/api/quiz/all`);
                if (!res.ok) throw new Error("Failed to fetch questions");
                const data = await res.json();
                setQuestions(data);
            } catch (err) {
                console.error(err);
            }
        };
        fetchQuestions();
    }, []);

    // DISTINCT over the question bank arrives unordered — show school order.
    const classes = sortClasses([...new Set(questions.map(q => q.class))]);
    const subjectsByClass = classes.reduce((acc, cls) => {
        const subjects = questions.filter(q => q.class === cls).map(q => q.subject);
        acc[cls] = [...new Set(subjects)];
        return acc;
    }, {});

    const filteredSubjects = formData.yearGroup ? subjectsByClass[formData.yearGroup] || [] : [];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value, subjects: name === "yearGroup" ? [] : formData.subjects });
    };

    const handleSubjectToggle = (subject) => {
        setFormData(prev => {
            const selected = prev.subjects || [];
            if (selected.includes(subject)) return { ...prev, subjects: selected.filter(s => s !== subject) };
            if (selected.length >= 3) {
                toast.error("You can only select up to 3 subjects.");
                return prev;
            }
            return { ...prev, subjects: [...selected, subject] };
        });
    };

    const handleSubmit = async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) return;

            const res = await fetch(`${API_BASE_URL}/api/free-users/details`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });

            if (!res.ok) {
                const errData = await res.json();
                toast.error(errData.message || "Failed to save details");
                return;
            }

            toast.success("Details saved successfully!");
            navigate("/free-dashboard");
        } catch (err) {
            console.error(err);
            toast.error("Error saving details");
        }
    };

    return (
        <div
            className="flex justify-center items-center min-h-screen py-4"
            style={{
                backgroundImage: `url(${bg})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                backgroundColor: '#2148C0'
            }}
        >
            <div className="flex flex-col bg-white/10 backdrop-blur-sm rounded-lg p-8 w-[93%] max-w-2xl">
                <h2 className="text-2xl font-bold text-white mb-4 text-center">Create Your Account</h2>

                {/* Year Group */}
                <div className="mb-6">
                    <label className="text-white font-medium mb-2 block">Select Year Group</label>
                    <select
                        name="yearGroup"
                        value={formData.yearGroup}
                        onChange={handleInputChange}
                        className="w-full p-2 rounded-lg"
                    >
                        <option value="">-- Select Year --</option>
                        {classes.map(cls => (
                            <option key={cls} value={cls}>{cls}</option>
                        ))}
                    </select>
                </div>

                {/* Subjects */}
                {formData.yearGroup && (
                    <div className="mb-6">
                        <label className="text-white font-medium mb-2 block">Select Subjects (up to 3)</label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                            {filteredSubjects.map(subject => (
                                <button
                                    key={subject}
                                    type="button"
                                    onClick={() => handleSubjectToggle(subject)}
                                    className={`p-3 rounded-lg border flex items-center justify-center text-white ${formData.subjects.includes(subject) ? 'bg-white/20 border-white' : 'border-white/30 hover:bg-white/3'}`}
                                >
                                    <BookOpen className="w-3 h-3 mr-2" />
                                    {subject}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                <button
                    onClick={handleSubmit}
                    disabled={!formData.yearGroup || formData.subjects.length === 0}
                    className="mt-4 w-full bg-white text-[#2148C0] font-medium py-2 rounded-lg hover:bg-gray-200 disabled:bg-white/20 disabled:text-white/30"
                >
                    Complete Registration
                </button>
            </div>
        </div>
    );
};

export default FreeUsersOnboardingPage;
